import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { useProjectModelError } from "@planzone/dto";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { createSelector } from "./selector";
import { State, Selector } from "./types";

const initialState: State = [];

function handleRequest(
  state: State,
  { projectModel, zone }: Boundaries["useProjectModel"]["request"],
) {
  const index = state.findIndex(
    (req) =>
      req.projectModel.uuid === projectModel.uuid &&
      req.zone.uuid === zone.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          projectModel,
          zone,
          request: setRequestStateToPending(),
          error: useProjectModelError,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          projectModel,
          zone,
          request: setRequestStateToPending(),
          error: useProjectModelError,
        },
      ];
}

function handleSuccess(
  state: State,
  { projectModel, zone }: Boundaries["useProjectModel"]["success"],
): State {
  const index = state.findIndex(
    (req) =>
      req.projectModel.uuid === projectModel.uuid &&
      req.zone.uuid === zone.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          projectModel,
          zone,
          request: setRequestStateToSuccess(),
          error: useProjectModelError,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          projectModel,
          zone,
          request: setRequestStateToSuccess(),
          error: useProjectModelError,
        },
      ];
}

function handleFailure(
  state: State,
  { projectModel, zone, error }: Boundaries["useProjectModel"]["failure"],
) {
  const index = state.findIndex(
    (req) =>
      req.projectModel.uuid === projectModel.uuid &&
      req.zone.uuid === zone.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          projectModel,
          zone,
          request: setRequestStateToFailure(),
          error,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          projectModel,
          zone,
          request: setRequestStateToFailure(),
          error,
        },
      ];
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.useProjectModelRequested]: handleRequest,
    [events.useProjectModelSucceeded]: handleSuccess,
    [events.useProjectModelFailed]: handleFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
